import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X, LogIn, LayoutDashboard, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { siteSections, primaryNav } from "@/data/siteMap";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null); // desktop click/hover
  const [mobileSection, setMobileSection] = useState(null); // mobile accordion
  const location = useLocation();
  const { user } = useAuth();
  const headerRef = useRef(null);

  // Close menus on route change
  useEffect(() => { setOpen(false); setOpenSection(null); setMobileSection(null); }, [location]);

  // Click outside closes desktop dropdown
  useEffect(() => {
    if (!openSection) return;
    const onDoc = (e) => {
      if (!headerRef.current) return;
      if (!headerRef.current.contains(e.target)) setOpenSection(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [openSection]);

  // Close on escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setOpenSection(null); setOpen(false); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const isClient = user && user.role === "client";
  const isAdmin = user && user.role === "admin";

  return (
    <header
      ref={headerRef}
      data-testid="primary-navbar"
      className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0B0C10]/90 border-b border-[#1F2833]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-2 shrink-0">
          <span className="font-display text-[#F5F5F7] text-xl font-bold tracking-tight">TraceElements</span>
          <span className="h-2 w-2 rounded-full bg-[#FF3355] te-pulse-dot shadow-[0_0_12px_#FF3355]"></span>
        </Link>

        {/* DESKTOP */}
        <nav className="hidden lg:flex items-center gap-0.5">
          {primaryNav.map((key) => {
            const section = siteSections[key];
            if (!section) return null;
            if (section.directLink) {
              return (
                <Link
                  key={key}
                  to={section.overviewHref || `/${key}`}
                  data-testid={`nav-${key}`}
                  onClick={() => setOpenSection(null)}
                  className="px-3 xl:px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
                >
                  {section.label}
                </Link>
              );
            }
            const isOpen = openSection === key;
            return (
              <button
                key={key}
                data-testid={`nav-${key}`}
                onClick={() => setOpenSection(isOpen ? null : key)}
                className={`px-3 xl:px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-1 ${
                  isOpen ? "text-[#FF3355]" : "text-[#C5C6C7] hover:text-[#F5F5F7]"
                }`}
              >
                {section.label}
                <ChevronDown size={12} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          {isClient && (
            <Link to="/portal" data-testid="nav-portal" className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#C5C6C7] hover:text-[#FF3355] inline-flex items-center gap-1.5">
              <LayoutDashboard size={12} /> Portal
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin" data-testid="nav-admin" className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#C5C6C7] hover:text-[#FF3355] inline-flex items-center gap-1.5">
              <LayoutDashboard size={12} /> Admin
            </Link>
          )}
          {!user && (
            <Link to="/login" data-testid="nav-login" className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#C5C6C7] hover:text-[#FF3355] inline-flex items-center gap-1.5">
              <LogIn size={12} /> Sign in
            </Link>
          )}
          <Link
            to="/terminal"
            data-testid="nav-launch-terminal-cta"
            className="ml-2 font-mono text-[11px] uppercase tracking-[0.2em] bg-[#B91C2C] text-[#F5F5F7] px-5 py-3 hover:bg-[#FF3355] transition-colors font-semibold whitespace-nowrap"
          >
            Launch Terminal →
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden text-[#F5F5F7] p-2"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Desktop dropdown */}
      {openSection && (
        <div
          className="hidden lg:block absolute inset-x-0 top-full bg-[#0B0C10] border-t border-b border-[#1F2833] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)]"
          data-testid={`megamenu-${openSection}`}
        >
          <MegaPanel section={siteSections[openSection]} sectionKey={openSection} onClose={() => setOpenSection(null)} />
        </div>
      )}

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-[#1F2833] bg-[#0B0C10]/95 px-6 py-6 space-y-2 max-h-[calc(100vh-5rem)] overflow-y-auto">
          {primaryNav.map((key) => {
            const s = siteSections[key];
            if (!s) return null;
            const overviewHref = s.overviewHref || `/${key}`;
            if (s.directLink) {
              return (
                <div key={key} className="border-b border-[#1F2833]">
                  <Link
                    to={overviewHref}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-${key}-mobile`}
                    className="w-full flex items-center justify-between py-3 font-mono text-xs uppercase tracking-[0.22em] text-emerald-400"
                  >
                    {s.label}
                  </Link>
                </div>
              );
            }
            const expanded = mobileSection === key;
            return (
              <div key={key} className="border-b border-[#1F2833]">
                <button
                  onClick={() => setMobileSection(expanded ? null : key)}
                  data-testid={`nav-${key}-mobile`}
                  className="w-full flex items-center justify-between py-3 font-mono text-xs uppercase tracking-[0.22em] text-[#C5C6C7]"
                >
                  {s.label}
                  <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180 text-[#FF3355]" : ""}`} />
                </button>
                {expanded && (
                  <div className="pb-4 space-y-1">
                    <MobileItemLink to={overviewHref} className="text-[#FF3355]">// {s.label} overview</MobileItemLink>
                    {s.items.map((it) => {
                      const to = it.href || `/${key}/${it.slug}`;
                      return (
                        <MobileItemLink
                          key={it.slug}
                          to={to}
                          testid={`nav-${key}-${it.slug}-mobile`}
                        >
                          {it.label}
                        </MobileItemLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {isClient && <Link to="/portal" data-testid="nav-portal-mobile" className="block py-3 font-mono text-xs uppercase tracking-[0.22em] text-[#C5C6C7]">Portal</Link>}
          {isAdmin && <Link to="/admin" data-testid="nav-admin-mobile" className="block py-3 font-mono text-xs uppercase tracking-[0.22em] text-[#C5C6C7]">Admin</Link>}
          {!user && <Link to="/login" data-testid="nav-login-mobile" className="block py-3 font-mono text-xs uppercase tracking-[0.22em] text-[#C5C6C7]">Sign in</Link>}

          <Link
            to="/terminal"
            data-testid="nav-launch-terminal-cta-mobile"
            className="mt-4 inline-block font-mono text-xs uppercase tracking-[0.22em] bg-[#B91C2C] text-[#F5F5F7] px-5 py-3 hover:bg-[#FF3355]"
          >
            Launch Terminal →
          </Link>
        </div>
      )}
    </header>
  );
}

function MobileItemLink({ to, children, testid, className = "" }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`block py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7] hover:text-[#F5F5F7] ${className}`}
    >
      {children}
    </Link>
  );
}

function MegaPanel({ section, sectionKey, onClose }) {
  const items = section.items || [];
  const overviewHref = section.overviewHref || `/${sectionKey}`;

  // Group items by their optional `group` field. If no groups, treat as single unnamed group.
  const hasGroups = items.some((it) => it.group);
  const groups = hasGroups
    ? [...new Set(items.map((it) => it.group))].map((name) => ({
        name,
        items: items.filter((it) => it.group === name),
      }))
    : [{ name: null, items }];

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-12 gap-10">
      <div className="col-span-12 lg:col-span-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B91C2C] mb-3">{section.landing.kicker}</div>
        <h3 className="font-display text-2xl font-bold tracking-tight text-[#F5F5F7]">{section.landing.title}</h3>
        <p className="mt-3 text-sm text-[#C5C6C7] leading-relaxed">{section.landing.tagline}</p>
        <Link
          to={overviewHref}
          onClick={onClose}
          data-testid={`megamenu-${sectionKey}-overview`}
          className="mt-6 inline-block font-mono text-xs uppercase tracking-[0.22em] text-[#FF3355] hover:text-[#F5F5F7] border-b border-[#FF3355]/40"
        >
          view all {section.label.toLowerCase()} →
        </Link>
      </div>
      <div className={`col-span-12 lg:col-span-9 grid grid-cols-1 md:grid-cols-2 ${hasGroups ? "" : `lg:grid-cols-3`} gap-8`}>
        {groups.map((g) => {
          // Split each group into 2 columns for scannability when many items
          const cols = g.items.length > 10 ? 2 : 1;
          const perCol = Math.ceil(g.items.length / cols);
          const columns = Array.from({ length: cols }, (_, i) => g.items.slice(i * perCol, (i + 1) * perCol));
          return (
            <div key={g.name || "flat"} className="space-y-3">
              {g.name && (
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400 border-b border-emerald-400/30 pb-2">
                  // {g.name}
                </div>
              )}
              <div className={`grid grid-cols-${cols} gap-x-6`}>
                {columns.map((col, ci) => (
                  <ul key={ci} className="space-y-2">
                    {col.map((it) => (
                      <li key={it.slug}>
                        <Link
                          to={it.href || `/${sectionKey}/${it.slug}`}
                          onClick={onClose}
                          data-testid={`megamenu-${sectionKey}-${it.slug}`}
                          className="block font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7] hover:text-[#FF3355] py-1.5 transition-colors"
                        >
                          {it.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
